(() => {
  'use strict';
  if (window.HomiHavenHeroSlider) return;
  const controllers = new WeakMap();

  function create(root) {
    if (controllers.has(root)) return;
    const slides = [...root.querySelectorAll('[data-hero-slide]')];
    if (slides.length < 2) return;
    const controls = root.querySelector('[data-hero-controls]');
    const dots = [...root.querySelectorAll('[data-hero-dot]')];
    const playButton = root.querySelector('[data-hero-play]');
    const status = root.querySelector('[data-hero-status]');
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const editor = !!window.Shopify?.designMode || document.body.dataset.designMode === 'true';
    const autoplay = root.dataset.autoplay === 'true';
    const interval = Math.max(4000, Number(root.dataset.interval) || 6000);
    const events = new AbortController();
    const on = (target, type, handler, options = {}) => target?.addEventListener(type, handler, { ...options, signal: events.signal });
    let current = 0, timer, userPaused = false, focusPaused = false, hovered = false, inView = true, pointer;
    const paused = () => userPaused || focusPaused || motion.matches || editor;

    function schedule() {
      clearTimeout(timer);
      if (playButton) {
        playButton.hidden = motion.matches || editor;
        playButton.setAttribute('aria-label', paused() ? 'Play slideshow' : 'Pause slideshow');
        playButton.querySelector('[data-hero-pause-icon]').toggleAttribute('hidden', paused());
        playButton.querySelector('[data-hero-play-icon]').toggleAttribute('hidden', !paused());
      }
      if (autoplay && !paused() && !hovered && inView && !document.hidden) {
        timer = setTimeout(() => show(current + 1), interval);
      }
    }

    function show(next, manual = false) {
      const target = (next + slides.length) % slides.length;
      if (!manual && slides[current].contains(document.activeElement)) {
        focusPaused = true;
        schedule();
        return;
      }
      if (manual) userPaused = true;
      slides.forEach((slide, index) => {
        slide.hidden = index !== target;
        slide.inert = index !== target;
        slide.classList.toggle('is-entering', index === target);
      });
      dots.forEach((dot, index) => {
        if (index === target) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      current = target;
      root.dataset.headerTone = slides[current].dataset.headerTone;
      root.dispatchEvent(new CustomEvent('hero:change', { bubbles: true }));
      if (manual) status.textContent = `Slide ${current + 1} of ${slides.length}`;
      schedule();
    }

    controls.hidden = false;
    on(controls, 'click', event => {
      const direction = event.target.closest('[data-hero-direction]');
      const dot = event.target.closest('[data-hero-dot]');
      if (direction) show(current + Number(direction.dataset.heroDirection), true);
      else if (dot) show(Number(dot.dataset.heroDot), true);
      else if (event.target.closest('[data-hero-play]')) {
        userPaused = !paused();
        focusPaused = false;
        schedule();
      }
    });
    on(root, 'keydown', event => {
      if (event.target !== root) return;
      const next = { ArrowLeft: current - 1, ArrowRight: current + 1, Home: 0, End: slides.length - 1 }[event.key];
      if (next === undefined) return;
      event.preventDefault();
      show(next, true);
    });
    on(root, 'pointerenter', event => {
      if (event.pointerType === 'mouse') { hovered = true; schedule(); }
    });
    on(root, 'pointerleave', () => { hovered = false; schedule(); });
    on(root, 'focusin', event => {
      if (!event.target.closest('[data-hero-play]')) { focusPaused = true; schedule(); }
    });
    on(root, 'pointerdown', event => {
      if (event.pointerType === 'touch' && !event.target.closest('a,button')) {
        pointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
      }
    }, { passive: true });
    on(root, 'pointerup', event => {
      if (!pointer || pointer.id !== event.pointerId) return;
      const dx = event.clientX - pointer.x, dy = event.clientY - pointer.y;
      pointer = null;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.3) show(current + (dx < 0 ? 1 : -1), true);
    }, { passive: true });
    on(root, 'pointercancel', () => { pointer = null; });
    on(document, 'visibilitychange', schedule);
    on(motion, 'change', schedule);
    const observer = new IntersectionObserver(entries => {
      inView = entries[0].isIntersecting;
      schedule();
    });
    observer.observe(root);
    controllers.set(root, {
      select: slide => { const index = slides.indexOf(slide); if (index >= 0) show(index, true); },
      first: () => show(0, true),
      destroy: () => { clearTimeout(timer); events.abort(); observer.disconnect(); controllers.delete(root); }
    });
    schedule();
  }

  function roots(scope) {
    return [...(scope.matches?.('[data-hero-slider]') ? [scope] : []), ...scope.querySelectorAll('[data-hero-slider]')];
  }
  const init = scope => roots(scope).forEach(create);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => roots(event.target).forEach(root => controllers.get(root)?.destroy()));
  document.addEventListener('shopify:block:select', event => {
    const slide = event.target.closest('[data-hero-slide]');
    if (slide) controllers.get(slide.closest('[data-hero-slider]'))?.select(slide);
  });
  document.addEventListener('shopify:section:select', event => roots(event.target).forEach(root => controllers.get(root)?.first()));
  window.HomiHavenHeroSlider = { init };
  init(document);
})();
